<template>
  <v-dialog v-model="dialog" max-width="500px">
    <v-card>
      <v-card-title class="text-h6">
        <v-icon left color="primary">mdi-clipboard-text</v-icon>
        Justificar Falta do Professor
      </v-card-title>

      <v-card-text>
        <div class="mb-4">
          <v-alert type="info" outlined dense class="mb-4">
            <div class="font-weight-medium">Professor:</div>
            <div>{{ professor?.nome || "Carregando..." }}</div>
          </v-alert>
        </div>

        <ValidationObserver ref="observer">
          <v-form @submit.prevent="confirmar">
            <ValidationProvider v-slot="{ errors }" name="justificativa" rules="required">
              <v-select
                v-model="form.justificativa_id"
                :items="justificativas"
                :item-value="(item) => item.id"
                :item-text="(item) => item.descricao"
                label="Selecione uma justificativa *"
                :error-messages="errors"
                outlined
                dense
                :loading="carregandoJustificativas"
                :disabled="salvando"
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title> Nenhuma justificativa encontrada </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </ValidationProvider>

            <v-file-input
              v-model="form.file"
              label="Anexar arquivo (opcional)"
              outlined
              dense
              show-size
              accept=".pdf"
              prepend-icon="mdi-paperclip"
              :disabled="salvando"
              :rules="[rules.tamanhoMaximo, rules.apenasPdf]"
              hint="Apenas arquivos PDF são permitidos (máx. 4MB)"
              persistent-hint
            >
              <template v-slot:selection="{ text }">
                <v-chip small label color="primary">
                  <v-icon left small>mdi-file-pdf</v-icon>
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
          </v-form>
        </ValidationObserver>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn text color="grey darken-1" @click="cancelar" :disabled="salvando">
          <v-icon left small>mdi-close</v-icon>
          Cancelar
        </v-btn>

        <v-btn
          color="success"
          @click="confirmar"
          :loading="salvando"
          :disabled="!form.justificativa_id"
        >
          <v-icon left small>mdi-check</v-icon>
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  name: "EModalJustificarFaltaProfessorPlanejamento",

  components: {
    ValidationObserver,
    ValidationProvider,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    planejamentoAulaId: {
      type: [Number, String],
      required: true,
    },
    professor: {
      type: Object,
      required: true,
    },
    justificativaId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      dialog: false,
      salvando: false,
      carregandoJustificativas: false,
      justificativas: [],
      form: {
        justificativa_id: null,
        file: null,
      },
      rules: {
        tamanhoMaximo: (file) =>
          !file || file.size <= 4 * 1024 * 1024 || "O arquivo deve ter no máximo 4MB",
        apenasPdf: (file) =>
          !file || file.type === "application/pdf" || "Apenas arquivos PDF são permitidos",
      },
    };
  },

  watch: {
    value: {
      handler(newVal) {
        this.dialog = newVal;
        if (newVal) {
          this.inicializarModal();
        } else {
          this.resetForm();
        }
      },
      immediate: true,
    },

    dialog(newVal) {
      if (!newVal) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    async inicializarModal() {
      this.form = {
        justificativa_id: this.justificativaId ? parseInt(this.justificativaId, 10) : null,
        file: null,
        professor_id: this.professor.id,
      };

      if (!this.justificativas.length) {
        const response = await this.$services.aulasService.justificativas();
        this.justificativas = response.justificativas;
      }
    },

    async carregarJustificativas() {
      this.carregandoJustificativas = true;
      try {
        const response = await this.$services.aulasService.justificativas();
        this.justificativas = response.justificativas;
      } catch (error) {
        this.$toast.danger("Erro ao carregar justificativas");
      } finally {
        this.carregandoJustificativas = false;
      }
    },

    async confirmar() {
      // Validar form
      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        this.$toast.warn("Por favor, corrija os erros no formulário");
        return;
      }

      if (!this.form.justificativa_id) {
        this.$toast.warn("É necessário selecionar uma justificativa");
        return;
      }

      this.salvando = true;

      try {
        await this.$services.gestoesDeAulasService.justificarFaltaProfessorPlanejamento(
          this.planejamentoAulaId,
          this.form
        );

        this.$toast.success("Justificativa salva com sucesso!");

        this.$emit("sucesso", {
          professor_id: this.professor.id,
          justificativa_id: this.form.justificativa_id,
          hasDocument: !!this.form.file,
        });

        this.fechar();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvando = false;
      }
    },

    cancelar() {
      this.fechar();
    },

    fechar() {
      this.dialog = false;
      setTimeout(() => {
        this.resetForm();
      }, 300);
    },

    resetForm() {
      this.form = {
        justificativa_id: null,
        file: null,
      };

      if (this.$refs.observer) {
        this.$refs.observer.reset();
      }
    },
  },
};
</script>

<style scoped>
.v-card-title {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.v-alert {
  border-left: 4px solid #2196f3 !important;
}
</style>
